import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Paper,
  Box,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Tooltip,
  TextField,
  FormControl,
  Autocomplete,
  DialogActions,
  Grid,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { useMaterialUIController } from "context";
import {
  StyledTypography,
  StyledAgGridContainer,
  StyledSnackbar,
  StyledAlert,
  inputStyle,
} from "components/StyledComponents/styledComponents.js";
import { subDialogContentStyle } from "components/Styles/approvalStyles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import URLs from "constants/urls";
import { StyledButton, ViewButton } from "components/StyledComponents/styledComponents";
import { CustomPaper } from "components/Styles/globalStyles";

export default function DSAInvoicePayment() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [invoiceData, setInvoiceData] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const jwt_token = Cookie.get("jwt_token");
  const MInput = inputStyle();
  const [selectedBank, setSelectedBank] = useState({ actledId: 0, actledName: "" });
  const [dsaDialogOpen, setDsaDialogOpen] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedGst, setSelectedGst] = useState("include");

  useEffect(() => {
    fetchInvoiceData();
    fetchBankAccountsData();
  }, []);

  const setAlert = (msg, type = "success") => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDateChange = (event) => setReceiptDate(event.target.value);

  const handleBankChange = (event, newValue) => setSelectedBank(newValue);

  const handleOpenDsaDialog = (data) => {
    if (!selectedBank.actledId) {
      setAlert("Please select a bank account to submit payment.", "error");
      return;
    }
    setPendingPaymentData(data);
    setDsaDialogOpen(true);
  };

  const handleCloseDsaDialog = () => {
    setDsaDialogOpen(false);
    setPendingPaymentData(null);
  };

  const handleConfirmDsaDialog = () => {
    if (pendingPaymentData) {
      handleConfirmPayment(pendingPaymentData);
    }
    handleCloseDsaDialog();
  };

  const fetchInvoiceData = async () => {
    try {
      const response = await fetch(`${URLs.headOfficeUnPaidInvoices}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setInvoiceData(data);
    } catch (error) {
      setAlert("Error fetching invoice data", "error");
    }
  };

  const fetchBankAccountsData = async () => {
    setLoading(true);
    const url = `${URLs.headOfficeBanksAcc}/${user.companyId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setBankAccounts(data);
    } catch (error) {
      setAlert("Failed to fetch bank accounts", "error");
    }
    setLoading(false);
  };

  const handlePrintInvoice = useCallback(
    async (invoiceNo) => {
      try {
        if (!invoiceNo) throw new Error("Invoice number is missing");
        const response = await fetch(`${URLs.headOfficeInvoiceView}/${invoiceNo}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${jwt_token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch invoice");
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        window.open(pdfUrl, "_blank");
      } catch (error) {
        console.error("Error fetching invoice:", error);
      }
    },
    [jwt_token]
  );

  const handleConfirmPayment = async (data) => {
    if (!selectedBank.actledId) {
      setAlert("Please select a bank account to submit payment.", "error");
      return;
    }
    if (!receiptDate) {
      setAlert("Please Select Amount Receipt Date", "error");
      return;
    }

    const netAmount = parseFloat(data.netDsaAmount) || 0;
    const tdsAmount = parseFloat(data.tdsAmount) || 0;

    const bodyData = {
      tranDate: receiptDate,
      invoiceNo: data.invoiceNo,
      khBankId: selectedBank.actledId,
      amount: data.amount,
      tdsAmount: tdsAmount,
      amountPaid: netAmount + tdsAmount,
      gstStatus: selectedGst === "include" ? "Y" : "N",
    };

    console.log("Payload:", bodyData);

    try {
      const response = await fetch(`${URLs.headOfficeConfirmDsaInvoicePay}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(bodyData),
      });
      const responseText = await response.text();
      if (response.ok) {
        setAlert(`${data.invoiceNo}: ${responseText}`, "success");
        fetchInvoiceData();
      } else {
        setAlert(`Failed to submit payment: ${responseText}`, "error");
      }
    } catch (error) {
      setAlert("An error occurred while submitting payment.", "error");
    }
  };

  const columnDefs = [
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      cellRenderer: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="View Invoice" arrow>
            <IconButton onClick={() => handlePrintInvoice(params.data.invoiceNo)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      headerName: "Date",
      field: "date",
      filter: true,
      minWidth: 90,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        const day = date.getDate().toString().padStart(2, "0");
        const month = date.toLocaleString("default", { month: "short" });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
      sort: "desc",
    },
    { headerName: "Invoice Number", field: "invoiceNo", sortable: true, filter: true, flex: 1 },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    { headerName: "DSA Code", field: "dsaCode", sortable: true, filter: true, flex: 1 },
    { headerName: "DSA Name", field: "dsaName", sortable: true, filter: true, flex: 1 },
    {
      headerName: "TDS",
      field: "tdsAmount",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) =>
        new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
        }).format(params.data.tdsAmount ?? 0),
    },
    {
      headerName: "Amount Paid",
      field: "netDsaAmount",
      flex: 1,
      valueFormatter: (params) =>
        new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
        }).format(params.data.netDsaAmount ?? 0),
    },
    {
      headerName: " ",
      field: "submit",
      width: 100,
      cellRenderer: (params) => (
        <ViewButton
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleOpenDsaDialog(params.data)}
        >
          Submit
        </ViewButton>
      ),
    },
  ];

  return (
    <DashboardLayout>
      {/* Loading Dialog */}
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Alert Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, mt: 2 }}>
        {/* Bank Selector */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "flex-start",
            mr: 1,
          }}
        >
          <FormControl sx={{ minWidth: 100, width: "100%", maxWidth: "200px" }}>
            <Autocomplete
              options={bankAccounts}
              getOptionLabel={(option) => `${option.actledName}`}
              value={selectedBank}
              onChange={handleBankChange}
              isOptionEqualToValue={(option, value) => option.actledId === value.actledId}
              PaperComponent={CustomPaper}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bank"
                  sx={{
                    height: 43,
                    ...MInput.b,
                    "& .MuiInputBase-root": { height: "100%", minHeight: 43 },
                  }}
                  InputLabelProps={{
                    sx: { color: MInput.a, "&.Mui-focused": { color: "red" } },
                  }}
                />
              )}
            />
          </FormControl>
        </Box>

        {/* AG Grid */}
        <Box sx={{ flexGrow: 1, mt: 2 }}>
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              rowData={invoiceData}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={10}
            />
          </StyledAgGridContainer>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={dsaDialogOpen} onClose={handleCloseDsaDialog}>
        <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
          Are you sure you want to proceed with this payment?
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Receipt Date */}
            <Grid item xs={12}>
              <TextField
                label="Select Date"
                type="date"
                value={receiptDate}
                onChange={handleDateChange}
                fullWidth
                sx={{ ...MInput.b }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* GST Dropdown */}
            <Grid item xs={12}>
              <Autocomplete
                options={[
                  { label: "Include", value: "include" },
                  { label: "Exclude", value: "exclude" },
                ]}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={
                  selectedGst
                    ? {
                        label: selectedGst.charAt(0).toUpperCase() + selectedGst.slice(1),
                        value: selectedGst,
                      }
                    : null
                }
                onChange={(event, newValue) =>
                  setSelectedGst(newValue ? newValue.value : "include")
                }
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select GST"
                    fullWidth
                    sx={{
                      height: 43,
                      ...MInput.b,
                      "& .MuiInputBase-root": { height: "100%", minHeight: 43 },
                    }}
                    InputLabelProps={{
                      sx: { color: MInput.a, "&.Mui-focused": { color: "red" } },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseDsaDialog} color="secondary">
            Cancel
          </StyledButton>
          <StyledButton onClick={handleConfirmDsaDialog} color="primary">
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
